import { useImperativeHandle, useRef, useState, useMemo, useEffect, useCallback } from "react";
import styles from "./ListCardContainer.module.css";
import i18n from "../../Services/i18n";

import ListCardFilterBar from "./ListCardFilterBar";

import horseIcon from "../../Assets/Icons/horse_black.png";
import clientIcon from "../../Assets/Icons/client_black.png";
import serviceIcon from "../../Assets/Icons/service_black.png";

import ListCardItem from "./ListCardItem";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import { useToasts } from "../../LayoutComponents/ToastContext";
import { FiX } from "react-icons/fi";

const ListCardContainer = ({
  isLoading,
  headerLabel,
  headerSubLabel,
  clearHeaderFilter,
  addButtonAction,
  addButtonDisabled = false,
  dataItems,
  refresh,
  onSelect,
  selectedItemExternalId,
  hideEmptyHorseCard,
  hideHeader,
  hideFilterBar,
  type,
  ref,
  storageId,
}) => {
  const cardRefs = useRef({});
  const toastCtx = useToasts();
  const storageKey = `listCardPrefs_${storageId}`;
  const displayModeKey = `ListCardDisplayMode_${storageId}`;

  const [displayMode, setDisplayMode] = useState(() => {
    const saved = localStorage.getItem(displayModeKey);
    return saved === "compact" || saved === "normal" ? saved : "normal";
  });

  const [isExpendedFilterBar, setIsExpendedFilterBar] = useState(false);

  const getDefaultSortKey = (type) => {
    switch (type) {
      case "client":
        return "firstName";
      case "horse":
        return "cardName";
      case "serviceoccurence":
        return "dateServiceOccurence";
      case "userservice":
        return "title";
      default:
        return "createdOn";
    }
  };

  const getInitialFilterState = () => {
    const saved = JSON.parse(localStorage.getItem(storageKey) || "null");

    const defaultSortKey = getDefaultSortKey(type);
    const initialState = {
      sortKey: saved?.sortKey ?? defaultSortKey,
      sortDirection: saved?.sortDirection ?? "asc",
      filterText: saved?.filterText ?? "",
      dateFrom: saved?.dateFrom ?? null,
      dateTo: saved?.dateTo ?? null,
      searchInTitle: saved?.searchInTitle ?? true,
      searchInDescription: saved?.searchInDescription ?? true,
      includeArchived: saved?.includeArchived ?? false,
    };

    if (!saved) {
      localStorage.setItem(storageKey, JSON.stringify(initialState));
    }

    return initialState;
  };

  const {
    sortKey: initialSortKey,
    sortDirection: initialSortDirection,
    filterText: initialFilterText,
    dateFrom: initialDateFrom,
    dateTo: initialDateTo,
    searchInTitle: initialSearchInTitle,
    searchInDescription: initialSearchInDescription,
    includeArchived: initialIncludeArchived,
  } = getInitialFilterState();

  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);
  const [filterText, setFilterText] = useState(initialFilterText);
  const [dateFrom, setDateFrom] = useState(initialDateFrom);
  const [dateTo, setDateTo] = useState(initialDateTo);
  const [searchInTitle, setSearchInTitle] = useState(initialSearchInTitle);
  const [searchInDescription, setSearchInDescription] = useState(initialSearchInDescription);
  const [includeArchived, setIncludeArchived] = useState(initialIncludeArchived);

  useImperativeHandle(ref, () => ({
    scrollToItem(externalId) {
      cardRefs.current[externalId]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    },
  }));

  useEffect(() => {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        sortKey,
        sortDirection,
        filterText,
        dateFrom,
        dateTo,
        searchInTitle,
        searchInDescription,
        includeArchived,
      }),
    );
  }, [
    sortKey,
    storageKey,
    sortDirection,
    filterText,
    dateFrom,
    dateTo,
    searchInTitle,
    searchInDescription,
    includeArchived,
  ]);

  const htmlToText = useCallback((html) => {
    const div = document.createElement("div");
    div.innerHTML = html ?? "";
    return div.textContent || div.innerText || "";
  }, []);

  const getItemDate = (item) => {
    switch (type) {
      case "client":
        return item.createdOn;
      case "horse":
        return item.createdOn;
      case "serviceoccurence":
        return item.dateServiceOccurence;
      case "userservice":
        return item.createdOn;
      default:
        return undefined;
    }
  };

  const filteredAndSortedItems = useMemo(() => {
    if (!dataItems) return [];

    let items = [...dataItems];

    if (filterText?.trim()) {
      const terms = filterText.toLowerCase().trim().split(/\s+/).filter(Boolean);

      items = items.filter((item) => {
        const titleText = `${item.cardName ?? item.title ?? ""}`.toLowerCase();
        const descriptionText = htmlToText(item.description).toLowerCase();

        let searchableText = "";

        if (searchInTitle) {
          searchableText += ` ${titleText}`;
        }

        if (searchInDescription) {
          searchableText += ` ${descriptionText}`;
        }

        searchableText = searchableText.trim();

        if (!searchableText) return false;

        return terms.every((term) => searchableText.includes(term));
      });
    }

    if (dateFrom || dateTo) {
      items = items.filter((item) => {
        const rawDate = getItemDate(item);
        if (!rawDate) return false;

        const itemDate = new Date(rawDate);
        if (Number.isNaN(itemDate.getTime())) return false;

        if (dateFrom) {
          const from = new Date(dateFrom);
          from.setHours(0, 0, 0, 0);
          if (itemDate < from) return false;
        }

        if (dateTo) {
          const to = new Date(dateTo);
          to.setHours(23, 59, 59, 999);
          if (itemDate > to) return false;
        }

        return true;
      });
    }

    if (sortKey) {
      items.sort((a, b) => {
        const valA = a[sortKey] ?? "";
        const valB = b[sortKey] ?? "";

        const isDateField = sortKey === "createdOn" || sortKey === "dateServiceOccurence";

        let compare = 0;

        if (isDateField) {
          const dateA = valA ? new Date(valA).getTime() : 0;
          const dateB = valB ? new Date(valB).getTime() : 0;
          compare = dateA > dateB ? 1 : dateA < dateB ? -1 : 0;
        } else {
          compare =
            typeof valA === "string"
              ? valA.localeCompare(valB)
              : valA > valB
                ? 1
                : valA < valB
                  ? -1
                  : 0;
        }

        return sortDirection === "asc" ? compare : -compare;
      });
    }

    if (!includeArchived) {
      items = items.filter((item) => !item.isArchived);
    }

    return items;
  }, [
    dataItems,
    sortKey,
    filterText,
    sortDirection,
    dateFrom,
    dateTo,
    searchInTitle,
    searchInDescription,
    htmlToText,
    includeArchived,
  ]);

  let emptyMsg = "";
  let addButtonLabel = "";
  let headerIcon = <></>;
  switch (type) {
    case "client":
      emptyMsg = i18n.t("BoardServiceClient.NoActiveClientCreated");
      addButtonLabel = i18n.t("BoardServiceClient.AddClientButton");
      headerIcon = <img src={clientIcon} alt="client icon" className={styles.headericon} />;
      break;
    case "horse":
      emptyMsg = i18n.t("BoardServiceClient.NoActiveHorseCreated");
      addButtonLabel = i18n.t("BoardServiceClient.AddHorseButton");
      headerIcon = <img src={horseIcon} alt="client icon" className={styles.headericon} />;
      break;
    case "serviceoccurence":
      emptyMsg = i18n.t("BoardServiceClient.EmptyServiceList");
      addButtonLabel = i18n.t("BoardServiceClient.AddServiceOccurenceButton");
      headerIcon = <img src={serviceIcon} alt="client icon" className={styles.headericon} />;
      break;
    case "userservice":
      emptyMsg = i18n.t("BoardServiceClient.EmptyServiceList");
      addButtonLabel = i18n.t("BoardServiceClient.AddServiceOccurenceButton");
      headerIcon = <img src={serviceIcon} alt="client icon" className={styles.headericon} />;
      break;
    default:
      emptyMsg = "";
  }

  const getEmptyListMessage = () => {
    if (type === "horse" && !selectedItemExternalId) {
      return (
        <div className={styles.noItemFound}>
          <span>{i18n.t("BoardServiceClient.NoClientSelected")}</span>
        </div>
      );
    } else if (type === "serviceoccurence" && !selectedItemExternalId) {
      return (
        <div className={styles.noItemFound}>
          <span>{i18n.t("BoardServiceClient.NoClientOrHorseSelected")}</span>
        </div>
      );
    } else {
      return (
        <div className={styles.noItemFound}>
          <span>{emptyMsg}</span>
        </div>
      );
    }
  };

  const clearFilters = () => {
    setFilterText("");
    setDateFrom(null);
    setDateTo(null);
    setSearchInTitle(true);
    setSearchInDescription(true);
    setIncludeArchived(false);
  };

  const EmptyHorseCard = () => {
    if (type === "horse" && !hideEmptyHorseCard) {
      if (selectedItemExternalId) {
        return (
          <ListCardItem
            id={-1}
            key={-1}
            dataItem={{
              externalId: -1,
              cardName: i18n.t("BoardServiceClient.ShowServiceWithNoHorse"),
            }}
            isSelected={-1 === selectedItemExternalId}
            onClick={onSelect}
            type="horse"
            showContextMenu={false}
            displayMode={displayMode}
          />
        );
      }
      return <></>;
    }
  };

  const getCardItems = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingIndicator}>
          <LoadingSpinner />
        </div>
      );
    } else {
      if (dataItems?.length === 0) {
        return getEmptyListMessage();
      } else {
        return filteredAndSortedItems.map((item) => (
          <ListCardItem
            ref={(el) => (cardRefs.current[item.externalId] = el)}
            id={item.externalId}
            key={item.externalId}
            dataItem={item}
            date={getItemDate(item)}
            refresh={async () => {
              if (typeof refresh === "function") refresh();
            }}
            isSelected={item.externalId === selectedItemExternalId}
            onClick={async (i) => {
              toastCtx.clearToasts();
              onSelect(i);
            }}
            type={type}
            displayMode={displayMode}
          />
        ));
      }
    }
  };

  const ListCardHeader = () => {
    if (!hideHeader) {
      return (
        <div className={styles.sectionHeader}>
          <div className={styles.headerContainer}>
            {headerIcon}
            <span className={styles.headerlabel}>{headerLabel}</span>
            {headerSubLabel && (
              <>
                <span
                  title={headerSubLabel}
                  style={{
                    fontSize: "16px",
                    fontStyle: "italic",
                    paddingLeft: "10px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    maxWidth: "60%",
                  }}>
                  {" "}
                  ({headerSubLabel})
                </span>
                {clearHeaderFilter && (
                  <FiX
                    className={styles.closeIcon}
                    onClick={() => {
                      clearHeaderFilter();
                    }}
                  />
                )}
              </>
            )}
          </div>
          {addButtonAction && (
            <button
              className={`${styles.headerButton} ${addButtonDisabled ? styles.disabled : ""}`}
              onClick={addButtonAction}
              disabled={addButtonDisabled}>
              {addButtonLabel}
            </button>
          )}
        </div>
      );
    }

    if (addButtonAction) {
      return (
        <div className={styles.leanSectionHeader}>
          <button className={styles.leanHeaderButton} onClick={addButtonAction}>
            {addButtonLabel}
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <ListCardHeader />
      <div className={styles.filterBarWrapper}>
        {!hideFilterBar && (
          <ListCardFilterBar
            type={type}
            sortKey={sortKey}
            sortDirection={sortDirection}
            filterText={filterText}
            dateFrom={dateFrom}
            dateTo={dateTo}
            includeArchived={includeArchived}
            searchInTitle={searchInTitle}
            searchInDescription={searchInDescription}
            onSortChange={(key, dir) => {
              setSortKey(key);
              setSortDirection(dir);
            }}
            onFilterChange={setFilterText}
            onIncludeArchived={setIncludeArchived}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onSearchInTitleChange={setSearchInTitle}
            onSearchInDescriptionChange={setSearchInDescription}
            onClearFilters={clearFilters}
            displayMode={displayMode}
            onDisplayModeChange={(mode) => {
              localStorage.setItem(displayModeKey, mode);
              setDisplayMode(mode);
            }}
            onExpendedChange={(isExpended) => setIsExpendedFilterBar(isExpended)}
          />
        )}
      </div>
      <div
        className={`${styles.sectionItemsList} ${!hideFilterBar ? styles.withFilterBar : ""} ${
          !hideFilterBar && isExpendedFilterBar ? styles.withFilterBarExpended : ""
        }`}>
        <EmptyHorseCard />
        {getCardItems()}
      </div>
    </>
  );
};

export default ListCardContainer;
